/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

class TestInstanceProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

    /**
	 * Get Test by ID
	 * @param {String|Number} pTestID - The Test ID to search for
	 * @return {Promise<void>}
	 * @async
	 */
	getTestByID(pTestID)
	{
		return new Promise((resolve, reject) =>
		{
			if (!pTestID)
			{
				this.pict.log.error('No Test ID provided.');
				return reject(new Error('No Test ID provided.'));
			}

			this.pict.HeadlightRestClient.getJSON(`Test/${pTestID}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Test: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Get the TestInstance by ID
	 * @param {String|Number} pTestInstanceID - The TestInstance ID to search for
	 * @return {Promise<void>}
	 * @async
	 */
	getTestInstanceByID(pTestInstanceID)
	{
		return new Promise((resolve, reject) =>
		{
			if (!pTestInstanceID)
			{
				this.pict.log.error('No TestInstance ID provided.');
				return reject(new Error('No TestInstance ID provided.'));
			}

			this.pict.HeadlightRestClient.getJSON(`TestInstance/${pTestInstanceID}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting TestInstance: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

    deleteTestInstanceByID(pTestInstanceID)
	{
		return new Promise((resolve, reject) =>
		{
			if (!pTestInstanceID)
			{
				this.pict.log.error('No TestInstance ID provided.');
				return reject(new Error('No TestInstance ID provided.'));
			}

			this.pict.HeadlightRestClient.deleteEntity('TestInstance', pTestInstanceID, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error deleting TestInstance: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

    async _getDocumentPolyJoinsForTestInstances(IDTestInstances)
    {
        return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('DocumentPolyJoin', `FBV~RemoteType~EQ~TestInstance~FBL~IDRemote~INN~${ IDTestInstances.join(',') }`, ()=>{}, ()=>{}, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting DocumentPolyJoins: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pRecords);
			});
		});
    }

    async _getDocumentPolyJoinsForSample(IDSample)
    {
        return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('DocumentPolyJoin', `FBV~RemoteType~EQ~Sample~FBL~IDRemote~INN~${ IDSample }`, ()=>{}, ()=>{}, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting DocumentPolyJoins: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pRecords);
			});
		});
    }

    async _getDocumentsByIDs(IDDocuments)
    {
        return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('Document', `FBL~IDDocument~INN~${ IDDocuments.join(',') }`, ()=>{}, ()=>{}, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Documents: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pRecords);
			});
		});
    }

    async _getDocumentMapForTestInstances(IDTestInstances)
    {
        const dpjs = await this._getDocumentPolyJoinsForTestInstances(IDTestInstances);
        const docs = await this._getDocumentsByIDs(dpjs.map(d => d.IDDocument));
        const docIDMap = {}
        for (let d of docs)
        {
            docIDMap[d.IDDocument] = d;
        }
        const tiDocMap = {};
        for (let t of IDTestInstances)
        {
            tiDocMap[t] = [];
        }
        for (let d of dpjs)
        {
            const doc = docIDMap[d.IDDocument];
            if (doc)
            {
                tiDocMap[d.IDRemote].push(docIDMap[d.IDDocument]);
            }
        }
        return tiDocMap;
    }

    async _getDocumentsForSample(IDSample)
    {
        const dpjs = await this._getDocumentPolyJoinsForSample(IDSample);
        return await this._getDocumentsByIDs(dpjs.map(d => d.IDDocument));
    }

    /**
	 * Get Tests by IDs
	 * @param {Array<number>} pIDTests - The Test IDs to search for
	 * @return {Promise<Array<Record<string, any>>>}
	 */
	async getTestsByIDs(pIDTests)
    {
        return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('Test', `FBL~IDTest~INN~${ pIDTests.join(',') }`, ()=>{}, ()=>{}, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Tests: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pRecords);
			});
		});
    }

    async getTestsByHashes(TestHashes)
    {
        return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('Test', `FBL~Hash~INN~${ TestHashes.join(',') }`, ()=>{}, ()=>{}, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting Tests: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pRecords);
			});
		});
    }

    async _getTestInstanceSampleJoinsForSample(IDSample)
    {
        return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('TestInstanceSampleJoin', `FBV~IDSample~EQ~${ IDSample }`, ()=>{}, ()=>{}, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting TestInstanceSampleJoins: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pRecords);
			});
		});
    }

    async _getTestInstancesByIDs(IDTestInstances)
    {
        return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('TestInstance', `FBL~IDTestInstance~INN~${ IDTestInstances.join(',') }`, ()=>{}, ()=>{}, (pError, pRecords) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error getting TestInstances: ${pError}`, pError);
					return reject(pError);
				}
				resolve(pRecords);
			});
		});
    }

    async _getTestInstancesForSample(IDSample)
    {
        const tisjs = await this._getTestInstanceSampleJoinsForSample(IDSample);
        return await this._getTestInstancesByIDs(tisjs.map(t => t.IDTestInstance));
    }

	async getTestInstanceBundleForSample(IDSample)
    {
        try
        {
            const TestInstances = await this._getTestInstancesForSample(IDSample);
            const TestInstanceDocumentMap = await this._getDocumentMapForTestInstances(TestInstances.map(t => t.IDTestInstance));
            const Tests = await this.getTestsByIDs(TestInstances.map(t => t.IDTest));
            const TestMap = {};
            for (let t of Tests)
            {
                TestMap[t.IDTest] = t;
            }

            // For now, let's also check Entity Data for Test Instance associations as well.
            // RIDOT doesn't have joins from TestInstance to Document currently for example.
            const tiGUIDMap = {};
            for (let t of TestInstances)
            {
                tiGUIDMap[t.GUIDTestInstance] = t.IDTestInstance;
            }
            const DocumentsForSample = await this._getDocumentsForSample(IDSample);
            for (let d of DocumentsForSample)
            {
                if (d.EntityData?.TestInstance?.length)
                {
                    for (let t of d.EntityData.TestInstance)
                    {
                        if (t.GUIDTestInstance.includes('{IDDocument}'))
                        {
                            t.GUIDTestInstance = t.GUIDTestInstance.replace('{IDDocument}', d.IDDocument);
                        }
                        if (tiGUIDMap[t.GUIDTestInstance] && TestInstanceDocumentMap[tiGUIDMap[t.GUIDTestInstance]] && !TestInstanceDocumentMap[tiGUIDMap[t.GUIDTestInstance]].map(d => d.IDDocument).includes(d.IDDocument))
                        {
                            TestInstanceDocumentMap[tiGUIDMap[t.GUIDTestInstance]].push(d)
                        }
                    }
                }
            }

            return { TestInstances, TestInstanceDocumentMap, TestMap };
        }
        catch (e)
        {
            console.log(`Failed to get Test Instance Bundle for Sample ${ IDSample }: `, e);
            return { TestInstances: [], TestInstanceDocumentMap: {}, TestMap: {} };
        }
    }
}

module.exports = TestInstanceProvider;
module.exports.default_configuration = {};
